<template>
  <div class="oracle-erp-layout">
    <!-- Oracle Cloud ERP 스타일 헤더 -->
    <header class="oc-header">
      <!-- 글로벌 헤더 (최상단) -->
      <div class="oc-global-bar">
        <div class="oc-global-left">
          <router-link to="/admin" class="oc-logo">
            <div class="oc-logo-icon">
              <i class="bi bi-grid-3x3-gap-fill"></i>
            </div>
            <div class="oc-logo-text">
              <span class="oc-logo-main">TOOLSTAR</span>
              <span class="oc-logo-divider">|</span>
              <span class="oc-logo-module">B2B 주문관리시스템</span>
            </div>
          </router-link>
        </div>
        <div class="oc-global-center">
          <div class="oc-search-box" :class="{ 'oc-search-active': showSearchResults }">
            <i class="bi bi-search"></i>
            <input
              type="text"
              v-model="searchQuery"
              @input="onSearchInput"
              @focus="onSearchFocus"
              @blur="onSearchBlur"
              @keydown.enter="goToFirstResult"
              @keydown.escape="closeSearch"
              placeholder="메뉴, 기능 검색..."
            />
            <i v-if="searchQuery" class="bi bi-x-circle oc-search-clear" @mousedown.prevent="clearSearch"></i>
          </div>
          <!-- 검색 결과 드롭다운 -->
          <div class="oc-search-dropdown" v-if="showSearchResults && filteredMenus.length > 0">
            <div class="oc-search-results">
              <div
                v-for="(item, index) in filteredMenus"
                :key="index"
                class="oc-search-item"
                @mousedown.prevent="goToMenu(item)"
              >
                <i :class="item.icon"></i>
                <div class="oc-search-item-info">
                  <span class="oc-search-item-name">{{ item.name }}</span>
                  <span class="oc-search-item-path">{{ item.parentName }} > {{ item.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="oc-search-dropdown oc-search-empty" v-if="showSearchResults && searchQuery && filteredMenus.length === 0">
            <div class="oc-search-no-result">
              <i class="bi bi-search"></i>
              <span>검색 결과가 없습니다</span>
            </div>
          </div>
        </div>
        <div class="oc-global-right">
          <div class="oc-quick-action" title="홈" @click="goHome">
            <i class="bi bi-house-door"></i>
          </div>
          <div class="oc-quick-action" :class="{ 'oc-active': showFavorites }" title="즐겨찾기" @click="toggleFavorites">
            <i class="bi bi-star"></i>
          </div>
          <!-- 즐겨찾기 드롭다운 -->
          <div class="oc-dropdown" v-if="showFavorites" @mouseleave="showFavorites = false">
            <div class="oc-dropdown-header">
              <span>즐겨찾기</span>
            </div>
            <div class="oc-dropdown-body">
              <div v-if="favorites.length === 0" class="oc-dropdown-empty">
                <i class="bi bi-star"></i>
                <span>즐겨찾기가 없습니다</span>
                <small>메뉴에서 ★를 클릭하여 추가하세요</small>
              </div>
              <div v-else class="oc-dropdown-list">
                <div
                  v-for="(fav, index) in favorites"
                  :key="index"
                  class="oc-dropdown-item"
                  @click="goToPath(fav.path)"
                >
                  <i :class="fav.icon"></i>
                  <span>{{ fav.name }}</span>
                  <i class="bi bi-x oc-remove-fav" @click.stop="removeFavorite(index)"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="oc-quick-action oc-has-badge" :class="{ 'oc-active': showNotifications }" title="알림" @click="toggleNotifications">
            <i class="bi bi-bell"></i>
            <span class="oc-badge-dot" v-if="unreadNotifications > 0"></span>
          </div>
          <!-- 알림 드롭다운 -->
          <div class="oc-dropdown oc-dropdown-notifications" v-if="showNotifications" @mouseleave="showNotifications = false">
            <div class="oc-dropdown-header">
              <span>알림</span>
              <span class="oc-badge-count" v-if="unreadNotifications > 0">{{ unreadNotifications }}</span>
              <a href="#" class="oc-mark-read" @click.prevent="markAllRead">모두 읽음</a>
            </div>
            <div class="oc-dropdown-body oc-notifications-list">
              <div v-if="notifications.length === 0" class="oc-dropdown-empty">
                <i class="bi bi-bell-slash"></i>
                <span>새로운 알림이 없습니다</span>
              </div>
              <div
                v-for="(noti, index) in notifications"
                :key="index"
                class="oc-notification-item"
                :class="{ 'oc-unread': !noti.read }"
                @click="readNotification(index)"
              >
                <div class="oc-noti-icon" :class="noti.type">
                  <i :class="noti.icon"></i>
                </div>
                <div class="oc-noti-content">
                  <span class="oc-noti-title">{{ noti.title }}</span>
                  <span class="oc-noti-desc">{{ noti.message }}</span>
                  <span class="oc-noti-time">{{ noti.time }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="oc-quick-action" title="설정" @click="goToSettings">
            <i class="bi bi-gear"></i>
          </div>
          <div class="oc-divider-v"></div>
          <div class="oc-user-area" @click="toggleUserMenu">
            <div class="oc-user-avatar">
              <i class="bi bi-person"></i>
            </div>
            <div class="oc-user-info">
              <span class="oc-user-name">{{ adminName }}</span>
              <span class="oc-user-role">Administrator</span>
            </div>
            <i class="bi bi-chevron-down oc-user-chevron"></i>
          </div>
          <!-- 사용자 메뉴 드롭다운 -->
          <div class="oc-dropdown oc-dropdown-user" v-if="showUserMenu" @mouseleave="showUserMenu = false">
            <div class="oc-user-profile">
              <div class="oc-user-avatar-lg">
                <i class="bi bi-person"></i>
              </div>
              <div class="oc-user-details">
                <span class="oc-user-name-lg">{{ adminName }}</span>
                <span class="oc-user-email">admin@toolstart.com</span>
              </div>
            </div>
            <div class="oc-dropdown-divider"></div>
            <div class="oc-dropdown-menu">
              <div class="oc-dropdown-item" @click="goToProfile">
                <i class="bi bi-person-circle"></i>
                <span>내 프로필</span>
              </div>
              <div class="oc-dropdown-item" @click="goToSettings">
                <i class="bi bi-gear"></i>
                <span>설정</span>
              </div>
            </div>
            <div class="oc-dropdown-divider"></div>
            <div class="oc-dropdown-menu">
              <div class="oc-dropdown-item oc-logout-item" @click="logout">
                <i class="bi bi-box-arrow-right"></i>
                <span>로그아웃</span>
              </div>
            </div>
          </div>
          <div class="oc-quick-action oc-logout" @click="logout" title="로그아웃">
            <i class="bi bi-box-arrow-right"></i>
          </div>
        </div>
      </div>
      <!-- 메인 네비게이션 (탭 스타일) -->
      <nav class="oc-main-nav">
        <ul class="oc-nav-tabs">
          <li class="oc-nav-tab"
              v-for="(menu, index) in mainMenus"
              :key="index"
              :class="{ active: activeMenu === menu.id }"
              @click="setActiveMenu(menu)">
            <i :class="menu.icon"></i>
            <span>{{ menu.name }}</span>
          </li>
        </ul>
      </nav>
    </header>

    <!-- 메인 컨테이너 -->
    <div class="oc-main-container">
      <!-- 좌측 사이드바 (Oracle 스타일) -->
      <aside class="oc-sidebar">
        <div class="oc-sidebar-header">
          <i class="bi bi-folder2-open"></i>
          <span>{{ getActiveMenuName() }}</span>
        </div>
        <nav class="oc-sidebar-nav">
          <ul class="oc-sidebar-menu">
            <li class="oc-sidebar-item"
                v-for="(subMenu, index) in getSubMenus()"
                :key="index">
              <router-link
                  :to="subMenu.path"
                  class="oc-sidebar-link"
                  active-class="active">
                <i :class="subMenu.icon"></i>
                <span>{{ subMenu.name }}</span>
              </router-link>
            </li>
          </ul>
        </nav>
        <div class="oc-sidebar-footer">
          <div class="oc-env-badge">
            <i class="bi bi-cloud-check"></i>
            <span>Production</span>
          </div>
        </div>
      </aside>

      <!-- 메인 콘텐츠 -->
      <main class="oc-content">
        <router-view />
      </main>
    </div>

    <!-- Oracle Cloud 스타일 푸터 -->
    <footer class="oc-footer">
      <div class="oc-footer-inner">
        <div class="oc-footer-left">
          <span class="oc-footer-brand">TOOLSTART ERP Cloud</span>
          <span class="oc-footer-sep">•</span>
          <span class="oc-footer-copy">&copy; 2025 Nebula System</span>
        </div>
        <div class="oc-footer-center">
          <div class="oc-footer-status">
            <i class="bi bi-circle-fill oc-status-dot"></i>
            <span>시스템 정상</span>
          </div>
        </div>
        <div class="oc-footer-right">
          <a href="#" class="oc-footer-link">도움말</a>
          <a href="#" class="oc-footer-link">개인정보처리방침</a>
          <a href="#" class="oc-footer-link">이용약관</a>
          <span class="oc-footer-version">v2.1.0</span>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'ShopLayout',
  data() {
    return {
      activeMenu: 'config',  // 기본 선택 메뉴
      // 검색 관련
      searchQuery: '',
      showSearchResults: false,
      // 드롭다운 상태
      showFavorites: false,
      showNotifications: false,
      showUserMenu: false,
      // 즐겨찾기 (localStorage에서 로드)
      favorites: [],
      // 알림 데이터
      notifications: [
        { title: '새 주문', message: '새로운 주문이 3건 접수되었습니다.', time: '5분 전', icon: 'bi bi-cart-check', type: 'order', read: false },
        { title: '재고 부족', message: '상품 SKU-1234의 재고가 10개 미만입니다.', time: '30분 전', icon: 'bi bi-exclamation-triangle', type: 'warning', read: false },
        { title: '배송 완료', message: '주문 #ORD-2024-001이 배송 완료되었습니다.', time: '1시간 전', icon: 'bi bi-truck', type: 'success', read: true }
      ],
      // 관리자 이름
      adminName: '관리자',
      mainMenus: [
        { id: 'config', name: '환경설정', icon: 'bi bi-gear' },
        { id: 'vendor', name: '거래처관리', icon: 'bi bi-building' },
        { id: 'product', name: '제품관리', icon: 'bi bi-box' },
        { id: 'inventory', name: '재고관리', icon: 'bi bi-stack' },
        { id: 'shoporders', name: '주문관리', icon: 'bi bi-cart' },
        // { id: 'order', name: '수주/견적관리', icon: 'bi bi-clipboard-check' },
        // { id: 'quotes', name: '견적관리', icon: 'bi bi-file-text' },
        // { id: 'shipping', name: '배송/출고관리', icon: 'bi bi-truck' },
        { id: 'promotion', name: '프로모션/쿠폰', icon: 'bi bi-tags' },
        { id: 'design', name: '디자인관리', icon: 'bi bi-palette' },
        { id: 'payment', name: '정산 및 외상결제관리', icon: 'bi bi-credit-card' },
        { id: 'stats', name: '매출/통계', icon: 'bi bi-graph-up' },
        // { id: 'review', name: '고객지원', icon: 'bi bi-currency-dollar' }
        { id: 'support', name: '고객지원', icon: 'bi bi-graph-up' }
      ],
      subMenus: {
        config: [
          { name: '기본 설정', path: '/admin/config/basic', icon: 'bi bi-sliders me-2' },
          { name: '사용자 관리', path: '/admin/config/users', icon: 'bi bi-people me-2' },
          { name: '권한 설정', path: '/admin/config/permissions', icon: 'bi bi-shield-lock me-2' },
          { name: '시스템 설정', path: '/admin/config/system', icon: 'bi bi-gear-wide-connected me-2' }
        ],
        vendor: [
          { name: '거래처 목록', path: '/admin/vendor/list', icon: 'bi bi-list-ul me-2' },
          { name: '거래처 등록', path: '/admin/vendor/register', icon: 'bi bi-plus-circle me-2' },
          { name: '거래처 등급', path: '/admin/vendor/level', icon: 'bi bi-plus-circle me-2' }
          // { name: '거래 내역', path: '/admin/vendor/transactions', icon: 'bi bi-journal-text me-2' }
        ],
        product: [
          { name: '제품 목록', path: '/admin/product/list', icon: 'bi bi-grid me-2' },
          // { name: '제품 등록', path: '/admin/product/register', icon: 'bi bi-plus-circle me-2' },
          { name: '카테고리 관리', path: '/admin/product/categories', icon: 'bi bi-diagram-3 me-2' },
          { name: '브랜드 관리', path: '/admin/product/brands', icon: 'bi bi-bookmark-star me-2' }
        ],
        inventory: [
          { name: '재고 현황', path: '/admin/inventory/status', icon: 'bi bi-clipboard-data me-2' }
          // { name: '입고 관리', path: '/admin/inventory/incoming', icon: 'bi bi-box-arrow-in-down me-2' },
          // { name: '출고 관리', path: '/admin/inventory/outgoing', icon: 'bi bi-box-arrow-right me-2' },
          // { name: '재고 조정', path: '/admin/inventory/adjust', icon: 'bi bi-pencil-square me-2' }
        ],
        shoporders: [
          { name: '주문 목록', path: '/admin/shoporders', icon: 'bi bi-list-ul me-2' },
          { name: '신규 주문', path: '/admin/shoporders/new', icon: 'bi bi-plus-circle me-2' },
          { name: '주문 처리중', path: '/admin/shoporders/processing', icon: 'bi bi-arrow-right-circle me-2' },
          { name: '배송 준비중', path: '/admin/shoporders/preparing', icon: 'bi bi-box-seam me-2' },
          { name: '배송중', path: '/admin/shoporders/shipping', icon: 'bi bi-truck me-2' },
          { name: '배송완료', path: '/admin/shoporders/delivered', icon: 'bi bi-check-circle me-2' },
          { name: '취소/반품/교환', path: '/admin/shoporders/cancelled', icon: 'bi bi-x-circle me-2' }
        ],
        // 다른 메뉴의 서브메뉴도 비슷한 방식으로 추가
        order: [
          { name: '수주 관리', path: '/admin/business/list', icon: 'bi bi-journal-plus me-2' },
          { name: '배송 현황', path: '/admin/business/shipping', icon: 'bi bi-truck me-2' },
          { name: '견적 목록', path: '/admin/business/estimates', icon: 'bi bi-file-earmark-text me-2' },
          { name: '견적 등록', path: '/admin/business/create', icon: 'bi bi-plus-circle me-2' }
        ],
        // quotes: [
        //   { name: '견적 목록', path: '/admin/quotes/estimates', icon: 'bi bi-file-earmark-text me-2' },
        //   { name: '견적 등록', path: '/admin/quotes/create', icon: 'bi bi-plus-circle me-2' }
        // ],

        shipping: [
          { name: '배송 목록', path: '/admin/shipping/list', icon: 'bi bi-truck me-2' },
          { name: '출고 목록', path: '/admin/shipping/dispatch', icon: 'bi bi-box-arrow-up-right me-2' },
          { name: '송장 등록', path: '/admin/shipping/invoice', icon: 'bi bi-receipt-cutoff me-2' },
          { name: '반품 관리', path: '/admin/shipping/returns', icon: 'bi bi-arrow-return-left me-2' },
          { name: '교환 관리', path: '/admin/shipping/exchanges', icon: 'bi bi-arrow-repeat me-2' }
        ],
        promotion: [
          // { name: '프로모션 관리', path: '/admin/promotion/list', icon: 'bi bi-megaphone me-2' },
          { name: '쿠폰 관리', path: '/admin/promotion/coupons', icon: 'bi bi-ticket-perforated me-2' }
        ],
        design: [
          { name: '메인디자인 관리', path: '/admin/design/main', icon: 'bi bi-house me-2' },
          // { name: '테마/레이아웃 관리', path: '/admin/design/theme', icon: 'bi bi-layout-text-window me-2' },
          { name: '배너 관리', path: '/admin/design/banner', icon: 'bi bi-image me-2' }
          // { name: '메뉴 관리', path: '/admin/design/menu', icon: 'bi bi-list me-2' },
          // { name: '로고/브랜딩', path: '/admin/design/branding', icon: 'bi bi-award me-2' },
          // { name: '모바일 디자인', path: '/admin/design/mobile', icon: 'bi bi-phone me-2' }
        ],
        payment: [
          { name: '정산 목록', path: '/admin/payment/settlements', icon: 'bi bi-cash-coin me-2' },
          { name: '외상 결제 현황', path: '/admin/payment/deferred', icon: 'bi bi-calendar-check me-2' },
          { name: '입금 내역', path: '/admin/payment/deposits', icon: 'bi bi-bank me-2' }
        ],
        stats: [
          { name: '매출 통계', path: '/admin/stats/sales', icon: 'bi bi-bar-chart-line me-2' },
          { name: '주문 통계', path: '/admin/stats/statsorders', icon: 'bi bi-clipboard-data me-2' },
          { name: '고객 분석', path: '/admin/stats/customers', icon: 'bi bi-people-fill me-2' }
        ],
        support: [
          // { name: '고객지원 대시보드', path: '/admin/support/dashboard', icon: 'bi bi-speedometer2 me-2' },
          { name: '공지사항 관리', path: '/admin/support/notice', icon: 'bi bi-megaphone me-2' },
          { name: 'FAQ 관리', path: '/admin/support/faq', icon: 'bi bi-question-circle me-2' },
          { name: '1:1 문의 관리', path: '/admin/support/inquiry', icon: 'bi bi-chat-dots me-2' }
          // { name: '리뷰 관리', path: '/admin/support/reviews', icon: 'bi bi-star me-2' },
          // { name: '고객 피드백', path: '/admin/support/feedback', icon: 'bi bi-chat-square-heart me-2' }
        ]
        // 나머지 메뉴의 서브메뉴도 필요에 따라 추가
      }
    }
  },
  computed: {
    // 검색 필터링된 메뉴
    filteredMenus() {
      if (!this.searchQuery.trim()) return [];
      const query = this.searchQuery.toLowerCase();
      const results = [];

      // 모든 메뉴와 서브메뉴를 검색
      this.mainMenus.forEach(mainMenu => {
        const subMenus = this.subMenus[mainMenu.id] || [];
        subMenus.forEach(sub => {
          if (sub.name.toLowerCase().includes(query) || mainMenu.name.toLowerCase().includes(query)) {
            results.push({
              ...sub,
              parentId: mainMenu.id,
              parentName: mainMenu.name
            });
          }
        });
      });

      return results.slice(0, 8); // 최대 8개만 표시
    },
    // 읽지 않은 알림 수
    unreadNotifications() {
      return this.notifications.filter(n => !n.read).length;
    }
  },
  methods: {
    // ========== 검색 기능 ==========
    onSearchInput() {
      this.showSearchResults = true;
    },
    onSearchFocus() {
      if (this.searchQuery) {
        this.showSearchResults = true;
      }
    },
    onSearchBlur() {
      setTimeout(() => {
        this.showSearchResults = false;
      }, 200);
    },
    clearSearch() {
      this.searchQuery = '';
      this.showSearchResults = false;
    },
    closeSearch() {
      this.showSearchResults = false;
    },
    goToMenu(item) {
      this.activeMenu = item.parentId;
      this.$router.push(item.path);
      this.searchQuery = '';
      this.showSearchResults = false;
    },
    goToFirstResult() {
      if (this.filteredMenus.length > 0) {
        this.goToMenu(this.filteredMenus[0]);
      }
    },

    // ========== 홈 버튼 ==========
    goHome() {
      this.activeMenu = 'config';
      this.$router.push('/admin');
    },

    // ========== 즐겨찾기 기능 ==========
    toggleFavorites() {
      this.showFavorites = !this.showFavorites;
      this.showNotifications = false;
      this.showUserMenu = false;
    },
    loadFavorites() {
      const saved = localStorage.getItem('admin_favorites');
      if (saved) {
        this.favorites = JSON.parse(saved);
      }
    },
    saveFavorites() {
      localStorage.setItem('admin_favorites', JSON.stringify(this.favorites));
    },
    addFavorite(menu) {
      if (!this.favorites.find(f => f.path === menu.path)) {
        this.favorites.push(menu);
        this.saveFavorites();
      }
    },
    removeFavorite(index) {
      this.favorites.splice(index, 1);
      this.saveFavorites();
    },
    goToPath(path) {
      this.$router.push(path);
      this.showFavorites = false;
    },

    // ========== 알림 기능 ==========
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      this.showFavorites = false;
      this.showUserMenu = false;
    },
    markAllRead() {
      this.notifications.forEach(n => n.read = true);
    },
    readNotification(index) {
      this.notifications[index].read = true;
    },

    // ========== 설정 버튼 ==========
    goToSettings() {
      this.activeMenu = 'config';
      this.$router.push('/admin/config/system');
      this.showUserMenu = false;
    },

    // ========== 사용자 메뉴 ==========
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu;
      this.showFavorites = false;
      this.showNotifications = false;
    },
    goToProfile() {
      this.activeMenu = 'config';
      this.$router.push('/admin/config/users');
      this.showUserMenu = false;
    },

    // ========== 로그아웃 ==========
    logout() {
      if (confirm('정말 로그아웃 하시겠습니까?')) {
        // 🔥 관리자 토큰만 삭제 (핵심!)
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('admin_user');
        localStorage.removeItem('auth_token');    // 🔥 이 줄 추가
        sessionStorage.removeItem('auth_token');
        // 🔥 사용자 정보도 삭제 (만약 있다면)
        localStorage.removeItem('user_info');

        console.log('🔐 관리자 로그아웃 완료');
        alert('로그아웃되었습니다.');

        // 🔥 페이지 새로고침으로 완전 초기화
        window.location.href = '/admin/login';
      }
    },
    setActiveMenu(menu) {
      this.activeMenu = menu.id;

      // 해당 메뉴의 첫 번째 서브메뉴로 라우팅 (선택 사항)
      const subMenus = this.getSubMenus();
      if (subMenus.length > 0) {
        this.$router.push(subMenus[0].path);
      }
    },
    getSubMenus() {
      return this.subMenus[this.activeMenu] || [];
    },
    getActiveMenuName() {
      const menu = this.mainMenus.find(m => m.id === this.activeMenu);
      return menu ? menu.name : '';
    }
  },
  created() {
    // URL 경로에 따라 초기 활성 메뉴 설정
    const path = this.$route.path;

    for (const menuId in this.subMenus) {
      const found = this.subMenus[menuId].some(subMenu => path.startsWith(subMenu.path));
      if (found) {
        this.activeMenu = menuId;
        break;
      }
    }

    // 즐겨찾기 로드
    this.loadFavorites();

    // 관리자 이름 로드
    const adminUser = localStorage.getItem('admin_user') || localStorage.getItem('adminUser');
    if (adminUser) {
      try {
        const user = JSON.parse(adminUser);
        this.adminName = user.name || user.username || '관리자';
      } catch (e) {
        this.adminName = '관리자';
      }
    }
  }
}
</script>

<style>
/* ============================================
   Oracle Cloud ERP 스타일 레이아웃
   ============================================ */

/* 전체 레이아웃 */
.oracle-erp-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: #f4f6f9;
  font-family: 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ============================================
   글로벌 헤더 (Oracle Cloud 스타일)
   ============================================ */
.oc-header {
  position: sticky;
  top: 0;
  z-index: 1000;
}

/* 최상단 글로벌 바 */
.oc-global-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  padding: 0 16px;
  background: #1a1a2e;
  border-bottom: 1px solid #2d2d44;
}

.oc-global-left,
.oc-global-center,
.oc-global-right {
  display: flex;
  align-items: center;
}

.oc-global-left {
  flex: 0 0 auto;
}

.oc-global-center {
  flex: 1;
  justify-content: center;
  padding: 0 40px;
  position: relative;
}

.oc-global-right {
  flex: 0 0 auto;
  gap: 4px;
  position: relative;
}

/* 로고 */
.oc-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 10px;
}

.oc-logo-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #c74634 0%, #e85a47 100%);
  border-radius: 6px;
}

.oc-logo-icon i {
  color: #fff;
  font-size: 16px;
}

.oc-logo-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.oc-logo-main {
  font-size: 16px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.5px;
}

.oc-logo-divider {
  color: #4a4a6a;
  font-weight: 300;
}

.oc-logo-module {
  font-size: 13px;
  font-weight: 400;
  color: #a0a0b8;
}

/* 검색 박스 */
.oc-search-box {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  height: 32px;
  padding: 0 12px;
  background: #2d2d44;
  border: 1px solid #3d3d5c;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.oc-search-box:focus-within {
  border-color: #c74634;
  box-shadow: 0 0 0 2px rgba(199, 70, 52, 0.2);
}

.oc-search-box i {
  color: #6a6a8a;
  font-size: 14px;
  margin-right: 8px;
}

.oc-search-box input {
  flex: 1;
  border: none;
  background: transparent;
  color: #ffffff;
  font-size: 13px;
  outline: none;
}

.oc-search-box input::placeholder {
  color: #6a6a8a;
}

.oc-search-clear {
  color: #6a6a8a;
  cursor: pointer;
  font-size: 14px;
  margin-left: 8px;
}

.oc-search-clear:hover {
  color: #ffffff;
}

/* 검색 결과 드롭다운 */
.oc-search-dropdown {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 400px;
  margin-top: 4px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  overflow: hidden;
}

.oc-search-results {
  max-height: 320px;
  overflow-y: auto;
}

.oc-search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.oc-search-item:hover {
  background: #f4f6f9;
}

.oc-search-item i {
  font-size: 16px;
  color: #c74634;
  width: 20px;
  text-align: center;
}

.oc-search-item-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.oc-search-item-name {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
}

.oc-search-item-path {
  font-size: 11px;
  color: #8a9199;
}

.oc-search-no-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: #8a9199;
}

.oc-search-no-result i {
  font-size: 24px;
  opacity: 0.5;
}

/* 퀵 액션 버튼 */
.oc-quick-action {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0a0b8;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}

.oc-quick-action:hover {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.08);
}

.oc-quick-action i {
  font-size: 16px;
}

.oc-has-badge .oc-badge-dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: #c74634;
  border-radius: 50%;
  border: 2px solid #1a1a2e;
}

.oc-logout:hover {
  color: #ff6b6b !important;
}

.oc-quick-action.oc-active {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
}

/* ============================================
   드롭다운 공통 스타일
   ============================================ */
.oc-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  min-width: 280px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  overflow: hidden;
}

.oc-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafbfc;
  border-bottom: 1px solid #e1e5eb;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
}

.oc-dropdown-body {
  padding: 8px 0;
  max-height: 300px;
  overflow-y: auto;
}

.oc-dropdown-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 16px;
  color: #8a9199;
  text-align: center;
}

.oc-dropdown-empty i {
  font-size: 28px;
  opacity: 0.4;
}

.oc-dropdown-empty small {
  font-size: 11px;
  color: #a0a0b8;
}

.oc-dropdown-list {
  padding: 0;
}

.oc-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 13px;
  color: #5a6872;
  transition: all 0.15s ease;
}

.oc-dropdown-item:hover {
  background: #f4f6f9;
  color: #1a1a2e;
}

.oc-dropdown-item i {
  font-size: 14px;
  width: 18px;
  text-align: center;
}

.oc-remove-fav {
  margin-left: auto;
  opacity: 0;
  color: #c74634;
}

.oc-dropdown-item:hover .oc-remove-fav {
  opacity: 1;
}

.oc-dropdown-divider {
  height: 1px;
  background: #e1e5eb;
  margin: 8px 0;
}

.oc-dropdown-menu {
  padding: 4px 0;
}

/* 알림 드롭다운 */
.oc-dropdown-notifications {
  width: 360px;
}

.oc-badge-count {
  background: #c74634;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
}

.oc-mark-read {
  font-size: 12px;
  font-weight: 400;
  color: #c74634;
  text-decoration: none;
  margin-left: auto;
}

.oc-mark-read:hover {
  text-decoration: underline;
}

.oc-notifications-list {
  padding: 0;
}

.oc-notification-item {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-left: 3px solid transparent;
}

.oc-notification-item:hover {
  background: #f4f6f9;
}

.oc-notification-item.oc-unread {
  background: #fff5f3;
  border-left-color: #c74634;
}

.oc-noti-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  flex-shrink: 0;
}

.oc-noti-icon.order {
  background: #e3f2fd;
  color: #1976d2;
}

.oc-noti-icon.warning {
  background: #fff3e0;
  color: #f57c00;
}

.oc-noti-icon.success {
  background: #e8f5e9;
  color: #388e3c;
}

.oc-noti-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.oc-noti-title {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
}

.oc-noti-desc {
  font-size: 12px;
  color: #5a6872;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.oc-noti-time {
  font-size: 11px;
  color: #a0a0b8;
}

/* 사용자 메뉴 드롭다운 */
.oc-dropdown-user {
  width: 240px;
}

.oc-user-profile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.oc-user-avatar-lg {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4a6cf7 0%, #6c7dff 100%);
  border-radius: 50%;
}

.oc-user-avatar-lg i {
  color: #fff;
  font-size: 20px;
}

.oc-user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.oc-user-name-lg {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
}

.oc-user-email {
  font-size: 12px;
  color: #8a9199;
}

.oc-logout-item {
  color: #c74634 !important;
}

.oc-logout-item:hover {
  background: #fff5f3 !important;
}

/* 구분선 */
.oc-divider-v {
  width: 1px;
  height: 24px;
  background: #3d3d5c;
  margin: 0 8px;
}

/* 사용자 영역 */
.oc-user-area {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.oc-user-area:hover {
  background: rgba(255, 255, 255, 0.08);
}

.oc-user-avatar {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4a6cf7 0%, #6c7dff 100%);
  border-radius: 50%;
}

.oc-user-avatar i {
  color: #fff;
  font-size: 14px;
}

.oc-user-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.oc-user-name {
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
}

.oc-user-role {
  font-size: 10px;
  color: #6a6a8a;
}

.oc-user-chevron {
  color: #6a6a8a;
  font-size: 10px;
  margin-left: 4px;
}

/* ============================================
   메인 네비게이션 (탭 스타일)
   ============================================ */
.oc-main-nav {
  background: #ffffff;
  border-bottom: 1px solid #e1e5eb;
  padding: 0 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.oc-nav-tabs {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 0;
  overflow-x: auto;
}

.oc-nav-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  color: #5a6872;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.oc-nav-tab:hover {
  color: #1a1a2e;
  background: #f8f9fa;
}

.oc-nav-tab.active {
  color: #c74634;
  border-bottom-color: #c74634;
  background: #fff5f3;
}

.oc-nav-tab i {
  font-size: 14px;
}

/* ============================================
   메인 컨테이너 (사이드바 + 콘텐츠)
   ============================================ */
.oc-main-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 사이드바 */
.oc-sidebar {
  width: 220px;
  min-width: 220px;
  background: #ffffff;
  border-right: 1px solid #e1e5eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.oc-sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  border-bottom: 1px solid #e1e5eb;
  background: #fafbfc;
}

.oc-sidebar-header i {
  color: #c74634;
  font-size: 16px;
}

.oc-sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.oc-sidebar-menu {
  list-style: none;
  margin: 0;
  padding: 0;
}

.oc-sidebar-item {
  margin: 2px 8px;
}

.oc-sidebar-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #5a6872;
  font-size: 13px;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.oc-sidebar-link:hover {
  color: #1a1a2e;
  background: #f4f6f9;
}

.oc-sidebar-link.active {
  color: #c74634;
  background: #fff5f3;
  font-weight: 500;
}

.oc-sidebar-link.active i {
  color: #c74634;
}

.oc-sidebar-link i {
  font-size: 14px;
  color: #8a9199;
  width: 18px;
  text-align: center;
}

.oc-sidebar-footer {
  padding: 12px 16px;
  border-top: 1px solid #e1e5eb;
  background: #fafbfc;
}

.oc-env-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #e6f4ea;
  color: #137333;
  font-size: 11px;
  font-weight: 500;
  border-radius: 12px;
}

.oc-env-badge i {
  font-size: 12px;
}

/* 콘텐츠 영역 */
.oc-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: #f4f6f9;
}

/* ============================================
   푸터 (Oracle Cloud 스타일)
   ============================================ */
.oc-footer {
  background: #ffffff;
  border-top: 1px solid #e1e5eb;
  padding: 0;
}

.oc-footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  font-size: 12px;
}

.oc-footer-left,
.oc-footer-center,
.oc-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.oc-footer-brand {
  font-weight: 600;
  color: #1a1a2e;
}

.oc-footer-sep {
  color: #c8cdd3;
}

.oc-footer-copy {
  color: #8a9199;
}

.oc-footer-status {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5a6872;
}

.oc-status-dot {
  font-size: 8px;
  color: #34a853;
}

.oc-footer-link {
  color: #5a6872;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s ease;
}

.oc-footer-link:hover {
  color: #c74634;
  background: #fff5f3;
}

.oc-footer-version {
  padding: 2px 8px;
  background: #f4f6f9;
  color: #5a6872;
  border-radius: 4px;
  font-size: 11px;
}

/* ============================================
   반응형 디자인
   ============================================ */
@media (max-width: 1200px) {
  .oc-nav-tab span {
    display: none;
  }
  .oc-nav-tab {
    padding: 12px 14px;
  }
  .oc-nav-tab i {
    font-size: 18px;
  }
  .oc-sidebar {
    width: 180px;
    min-width: 180px;
  }
}

@media (max-width: 992px) {
  .oc-search-box {
    max-width: 250px;
  }
  .oc-user-info {
    display: none;
  }
  .oc-sidebar {
    width: 60px;
    min-width: 60px;
  }
  .oc-sidebar-header span,
  .oc-sidebar-link span,
  .oc-env-badge span {
    display: none;
  }
  .oc-sidebar-header {
    justify-content: center;
    padding: 16px 8px;
  }
  .oc-sidebar-link {
    justify-content: center;
    padding: 12px 8px;
  }
  .oc-env-badge {
    padding: 6px;
    border-radius: 50%;
  }
}

@media (max-width: 768px) {
  .oc-global-center {
    display: none;
  }
  .oc-logo-module,
  .oc-logo-divider {
    display: none;
  }
  .oc-footer-inner {
    flex-direction: column;
    gap: 8px;
    padding: 12px 16px;
  }
  .oc-footer-center {
    order: -1;
  }
}
</style>